import { startRegistration } from "@simplewebauthn/browser";
import QRCode from "qrcode";
import { useMemo, useState, type FormEvent } from "react";
import { NavLink, Navigate, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rootApi, errorText, type Account, type Me } from "../api";
import { ConfirmDialog, Dialog } from "../dialog";
import { Banner, toast, useEditor } from "../editor";
import { Field, SelectField, TextField } from "../fields";
import { Avatar, go, useSession } from "../session";
import { Annotated, Badge, Card, Icon, Tabs } from "../ui";

/** Your Mivio login: one for every business, so it lives above them (and opens inside whichever you're in). */

interface Passkey { id: string; name: string; backedUp: boolean; createdAt: string; lastUsedAt: string | null }
interface SessionRow { id: string; browser: string; method: string; current: boolean; createdAt: string; lastSeenAt: string }
interface TrustedRow { id: string; browser: string; createdAt: string; expiresAt: string }
interface EventRow { kind: string; detail: Record<string, unknown>; at: string }

const EVENT_TEXT: Record<string, string> = {
  sign_up_started: "Login created",
  email_verified: "Email confirmed",
  signed_in: "Logged in",
  sign_in_failed: "Wrong password entered",
  password_changed: "Password changed",
  password_reset_requested: "Password reset asked for",
  password_reset: "Password reset",
  authenticator_added: "Authenticator app turned on",
  authenticator_removed: "Authenticator app turned off",
  backup_codes_replaced: "New backup codes made",
  backup_code_used: "Backup code used",
  passkey_added: "Passkey added",
  passkey_removed: "Passkey removed",
  session_ended: "Logged out a browser",
  signed_out_elsewhere: "Logged out everywhere else",
};

const TABS = [
  { key: "general", label: "General", icon: Icon.people },
  { key: "security", label: "Security", icon: Icon.lock },
] as const;

function useWhen(account: Account) {
  return useMemo(() => {
    const f = new Intl.DateTimeFormat(account.locale || "en-AU", { dateStyle: "medium", timeStyle: "short", timeZone: account.timeZone || undefined });
    return (iso: string | null) => (iso ? f.format(new Date(iso)) : "never");
  }, [account.locale, account.timeZone]);
}

export function AccountPage() {
  const { tab } = useParams();
  const nav = useNavigate();
  const { me } = useSession();
  if (!me) return null;
  if (tab !== "general" && tab !== "security") return <Navigate to="/account/general" replace />;
  const here = TABS.find((t) => t.key === tab)!;
  return (
    <div className="settings acct">
      <aside className="settings__side">
        <div className="settings__club">
          <Avatar name={me.account.name} className="acct__me" />
          <span>
            <strong>{me.account.name}</strong>
            <small>{me.account.email}</small>
          </span>
        </div>
        <nav className="settings__nav" aria-label="Your login">
          {TABS.map((t) => (
            <NavLink key={t.key} to={`/account/${t.key}`} className={({ isActive }) => (isActive ? "is-active" : "")}>
              <t.icon />
              <span>{t.label}</span>
              <Icon.chevR />
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="settings__main">
        <div className="acct__tabs">
          <Tabs tabs={TABS.map(({ key, label }) => ({ key, label }))} value={tab} onChange={(k) => nav(`/account/${k}`)} />
        </div>
        <h1 className="settings__title">
          <here.icon />
          {here.label}
        </h1>
        {tab === "general" ? <General me={me} /> : <Security me={me} />}
      </div>
    </div>
  );
}

/* ---------- General ---------- */

function General({ me }: { me: Me }) {
  const session = useSession();
  const zones = useMemo(() => Intl.supportedValuesOf("timeZone").map((z) => ({ value: z, label: z.replace(/_/g, " ") })), []);
  const currencies = useMemo(() => Intl.supportedValuesOf("currency").map((c) => ({ value: c, label: c })), []);
  const a = me.account;
  const ed = useEditor({
    initial: { name: a.name, timeZone: a.timeZone, currency: a.currency },
    revision: 0,
    savedText: "Details saved",
    save: async (draft) => {
      await rootApi("/me", { method: "PATCH", json: { name: draft.name.trim(), timeZone: draft.timeZone, currency: draft.currency } });
      await session.refresh();
      return { document: { ...draft, name: draft.name.trim() }, revision: 0 };
    },
  });
  return (
    <>
      {ed.leaveGuard}
      {ed.notice && <Banner tone={ed.notice.tone} title={ed.notice.title}>{ed.notice.text}</Banner>}
      <Annotated title="Details" text="Your name and how Mivio shows times and money to you. Every business you're in sees the same name.">
        <Card flush>
          <div className="acct-rows">
            <div className="acct-row acct-row--fields">
              <TextField label="Name" value={ed.draft.name} maxLength={120} onChange={(v) => ed.set("name", v)} error={ed.errors.name} help="" />
            </div>
            <div className="acct-row">
              <span className="acct-row__text">
                <small>Email</small>
                <span>{a.email}</span>
              </span>
              <span className="acct-row__hint">Your email is your login.{a.hq ? " You're part of Mivio HQ." : ""}</span>
            </div>
            <div className="acct-row acct-row--fields">
              <div className="grid-fields">
                <SelectField label="Time zone" value={ed.draft.timeZone} options={zones} onChange={(v) => ed.set("timeZone", v)} error={ed.errors.timeZone} />
                <SelectField label="Currency" value={ed.draft.currency} options={currencies} onChange={(v) => ed.set("currency", v)} error={ed.errors.currency} />
              </div>
              <p className="field__help">Used where a business hasn't set its own, such as costs Mivio shows you.</p>
            </div>
          </div>
        </Card>
      </Annotated>
    </>
  );
}

/* ---------- Security ---------- */

function Security({ me }: { me: Me }) {
  const session = useSession();
  const when = useWhen(me.account);
  const refresh = () => void session.refresh();
  return (
    <>
      <Passkeys when={when} onChange={refresh} />
      <TwoStep me={me} onChange={refresh} />
      <Password />
      <Browsers when={when} onChange={refresh} />
      <Activity when={when} />
    </>
  );
}

function Passkeys({ when, onChange }: { when: (iso: string | null) => string; onChange: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["me", "passkeys"], queryFn: () => rootApi<{ passkeys: Passkey[] }>("/me/passkeys").then((r) => r.passkeys) });
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<Passkey | null>(null);
  const [removing, setRemoving] = useState<Passkey | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = () => void qc.invalidateQueries({ queryKey: ["me", "passkeys"] });
  const add = async () => {
    setError(null);
    try {
      const { challenge, options } = await rootApi<{ challenge: string; options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>("/me/passkeys/options", { method: "POST" });
      const response = await startRegistration({ optionsJSON: options });
      await rootApi("/me/passkeys", { method: "POST", json: { challenge, response } });
      toast("Passkey added");
      reload();
      onChange();
    } catch (err) {
      if (err instanceof Error && (err.name === "NotAllowedError" || err.name === "AbortError")) return;
      if (err instanceof Error && err.name === "InvalidStateError") return setError("This device already has a passkey for your login.");
      setError(errorText(err));
    }
  };
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      toast(done);
      reload();
      onChange();
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setBusy(false);
      setRenaming(null);
      setRemoving(null);
    }
  };
  const rows = q.data ?? [];
  return (
    <Annotated title="Passkeys" badge={<Badge tone="info">Recommended</Badge>} text="Log in with Face ID, Touch ID, Windows Hello, your phone or a security key instead of a password and code.">
      <Card>
        {error && <Banner tone="critical" title="That did not work">{error}</Banner>}
        {rows.length > 0 && (
          <ul className="acct-items">
            {rows.map((p) => (
              <li key={p.id}>
                <Icon.key />
                <span>
                  <strong>{p.name}</strong>
                  <small>
                    {p.backedUp ? "Synced across devices · " : ""}Added {when(p.createdAt)} · {p.lastUsedAt ? `last used ${when(p.lastUsedAt)}` : "not used yet"}
                  </small>
                </span>
                <button type="button" className="btn btn--sm btn--plain" onClick={() => { setName(p.name); setRenaming(p); }}>Rename</button>
                <button type="button" className="btn btn--sm btn--danger-ghost" onClick={() => setRemoving(p)}>Remove</button>
              </li>
            ))}
          </ul>
        )}
        <button type="button" className="acct-add" onClick={() => void add()}>
          <Icon.plus />
          {rows.length ? "Add another passkey" : "Add a passkey"}
        </button>
      </Card>
      {renaming && (
        <Dialog title="Rename passkey" onClose={() => setRenaming(null)} busy={busy}>
          <form onSubmit={(e) => { e.preventDefault(); void run(() => rootApi(`/me/passkeys/${encodeURIComponent(renaming.id)}`, { method: "PATCH", json: { name: name.trim() } }), "Passkey renamed"); }}>
            <TextField label="Name" value={name} maxLength={60} onChange={setName} help="So you can tell your devices apart." />
            <div className="modal__actions">
              <button type="button" className="btn" onClick={() => setRenaming(null)} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={busy || !name.trim()}>Save</button>
            </div>
          </form>
        </Dialog>
      )}
      <ConfirmDialog
        open={!!removing}
        title="Remove this passkey?"
        text={`You won't be able to log in with “${removing?.name ?? ""}” any more.`}
        confirmLabel="Remove"
        tone="danger"
        busy={busy}
        onConfirm={() => removing && void run(() => rootApi(`/me/passkeys/${encodeURIComponent(removing.id)}`, { method: "DELETE" }), "Passkey removed")}
        onCancel={() => setRemoving(null)}
      />
    </Annotated>
  );
}

function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = `Mivio backup codes\nEach code works once.\n\n${codes.join("\n")}\n`;
  return (
    <>
      <Banner tone="info" title="Save these backup codes">Keep them somewhere safe, like a password manager. Each works once, and you won't see them again.</Banner>
      <div className="twostep-codes">
        <ol>
          {codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ol>
        <div className="twostep-codes__actions">
          <button type="button" className="btn btn--sm" onClick={() => void navigator.clipboard?.writeText(text).then(() => toast("Backup codes copied"), () => toast("Copy did not work. Write them down instead.", "error"))}>
            <Icon.copy /> Copy
          </button>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => {
              const a = document.createElement("a");
              a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
              a.download = "mivio-backup-codes.txt";
              a.click();
              setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            }}
          >
            Download
          </button>
          <button type="button" className="btn btn--sm btn--primary" onClick={onDone}>I've saved them</button>
        </div>
      </div>
    </>
  );
}

function TwoStep({ me, onChange }: { me: Me; onChange: () => void }) {
  const s = me.security;
  const [setup, setSetup] = useState<{ setupId: string; secret: string; qr: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [removing, setRemoving] = useState(false);
  const [newCodes, setNewCodes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attempt = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const start = () =>
    attempt(async () => {
      const r = await rootApi<{ setupId: string; secret: string; uri: string }>("/me/authenticator", { method: "POST" });
      setSetup({ setupId: r.setupId, secret: r.secret, qr: await QRCode.toDataURL(r.uri, { margin: 1, width: 176 }) });
      setCode("");
    });
  const confirm = (e: FormEvent) => {
    e.preventDefault();
    void attempt(async () => {
      const r = await rootApi<{ backupCodes: string[] }>("/me/authenticator/confirm", { method: "POST", json: { setupId: setup!.setupId, code: code.trim() } });
      setSetup(null);
      setCode("");
      setCodes(r.backupCodes);
      toast("Authenticator app turned on");
    });
  };
  const remove = (e: FormEvent) => {
    e.preventDefault();
    void attempt(async () => {
      await rootApi("/me/authenticator/remove", { method: "POST", json: { code: code.trim() } });
      setRemoving(false);
      setCode("");
      toast("Authenticator app turned off");
      onChange();
    });
  };

  return (
    <Annotated
      title="Two-step sign-in"
      badge={s.authenticator ? <Badge tone="success">Authenticator app</Badge> : <Badge tone="info">Email codes</Badge>}
      text="After your password, Mivio asks for a second step. Without an authenticator app, that's a code sent to your email."
    >
      <Card title="Authenticator app">
        {error && <Banner tone="critical" title="That did not work">{error}</Banner>}
        {codes ? (
          <BackupCodes codes={codes} onDone={() => { setCodes(null); onChange(); }} />
        ) : setup ? (
          <form onSubmit={confirm}>
            <div className="twostep-app">
              <img className="twostep-qr" src={setup.qr} alt="QR code for your authenticator app" width="176" height="176" />
              <div>
                <span className="twostep-app__label">Scan the code, or enter this key</span>
                <code className="twostep-app__key">{setup.secret.match(/.{1,4}/g)?.join(" ")}</code>
              </div>
            </div>
            <Field label="6-digit code from the app">
              {(id, d) => <input id={id} aria-describedby={d} className="twostep-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} autoFocus />}
            </Field>
            <div className="twostep-row">
              <button type="submit" className="btn btn--primary" disabled={busy || code.replace(/\D/g, "").length !== 6}>Turn on</button>
              <button type="button" className="btn btn--plain" onClick={() => setSetup(null)}>Cancel</button>
            </div>
          </form>
        ) : removing ? (
          <form onSubmit={remove}>
            <Field label="Code from your authenticator app, or a backup code">
              {(id, d) => <input id={id} aria-describedby={d} className="twostep-code" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />}
            </Field>
            <div className="twostep-row">
              <button type="submit" className="btn btn--danger" disabled={busy || !code.trim()}>Turn off authenticator app</button>
              <button type="button" className="btn btn--plain" onClick={() => setRemoving(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <ul className="twostep-list">
            <li>
              <Icon.phone />
              <span>
                <strong>{s.authenticator ? "On" : "Not set up"}</strong>
                <small>Codes from an app on your phone. Stronger than email codes.</small>
              </span>
              {s.authenticator ? (
                <button type="button" className="btn btn--sm btn--danger-ghost" onClick={() => { setRemoving(true); setCode(""); }}>Turn off</button>
              ) : (
                <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void start()}>Set up</button>
              )}
            </li>
            {s.authenticator && (
              <li>
                <Icon.lock />
                <span>
                  <strong>Backup codes</strong>
                  <small>{s.backupCodesLeft} left. Use one if you lose your phone.</small>
                </span>
                <button type="button" className="btn btn--sm" onClick={() => setNewCodes(true)}>Make new codes</button>
              </li>
            )}
          </ul>
        )}
      </Card>
      <ConfirmDialog
        open={newCodes}
        title="Make new backup codes?"
        text="Your old ones stop working."
        confirmLabel="Make new codes"
        busy={busy}
        onConfirm={() =>
          void attempt(async () => {
            setCodes((await rootApi<{ backupCodes: string[] }>("/me/backup-codes", { method: "POST" })).backupCodes);
            setNewCodes(false);
          })
        }
        onCancel={() => setNewCodes(false)}
      />
    </Annotated>
  );
}

function Password() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await rootApi("/me/password", { method: "POST", json: { currentPassword: current, newPassword: next } });
      setOpen(false);
      setCurrent("");
      setNext("");
      toast("Password changed. Every other browser was logged out.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Annotated title="Password" text="One password for every business you're in.">
      <Card>
        <button type="button" className="btn" onClick={() => { setError(null); setOpen(true); }}>
          Change your password
        </button>
      </Card>
      {open && (
        <Dialog title="Change your password" onClose={() => setOpen(false)} busy={busy}>
          <form onSubmit={(e) => void submit(e)}>
            <p>Every other browser is logged out. You stay logged in on this one.</p>
            {error && <Banner tone="critical" title="Your password wasn't changed">{error}</Banner>}
            <Field label="Current password">
              {(id, d) => <input id={id} aria-describedby={d} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus />}
            </Field>
            <Field label="New password" help="At least 10 characters. A short phrase works well." error={next && next.length < 10 ? "Use at least 10 characters." : undefined}>
              {(id, d) => <input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} />}
            </Field>
            <div className="modal__actions">
              <button type="button" className="btn" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={busy || !current || next.length < 10}>{busy ? "Changing…" : "Change password"}</button>
            </div>
          </form>
        </Dialog>
      )}
    </Annotated>
  );
}

function Browsers({ when, onChange }: { when: (iso: string | null) => string; onChange: () => void }) {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ["me", "sessions"], queryFn: () => rootApi<{ sessions: SessionRow[] }>("/me/sessions").then((r) => r.sessions) });
  const trusted = useQuery({ queryKey: ["me", "trusted"], queryFn: () => rootApi<{ browsers: TrustedRow[] }>("/me/trusted-browsers").then((r) => r.browsers) });
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const reload = () => {
    void qc.invalidateQueries({ queryKey: ["me", "sessions"] });
    void qc.invalidateQueries({ queryKey: ["me", "trusted"] });
  };
  const run = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await fn();
      toast(done);
      reload();
      onChange();
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setBusy(null);
      setAll(false);
    }
  };
  const list = sessions.data ?? [];
  const others = list.filter((s) => !s.current).length;
  return (
    <Annotated title="Devices" text="You're logged in on these browsers. If you don't recognise one, log it out and change your password.">
      <Card
        title="Logged in"
        flush
        action={others > 0 ? <button type="button" className="btn btn--sm btn--plain" disabled={!!busy} onClick={() => setAll(true)}>Log out of all other devices</button> : undefined}
      >
        {sessions.isPending ? (
          <p className="acct-pad muted">Loading devices…</p>
        ) : (
          <ul className="acct-devices">
            {list.map((s) => (
              <li key={s.id}>
                <Icon.desktop />
                <span className="acct-devices__text">
                  <strong>
                    {s.browser}
                    {s.current && <Badge tone="info">This device</Badge>}
                  </strong>
                  <small>Logged in {when(s.createdAt)} · active {when(s.lastSeenAt)}</small>
                </span>
                {!s.current && (
                  <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void run(s.id, () => rootApi(`/me/sessions/${s.id}`, { method: "DELETE" }), "Device logged out")}>
                    {busy === s.id ? "Logging out…" : "Log out"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {(trusted.data ?? []).length > 0 && (
        <Card title="Trusted browsers" flush foot="These skip the second step until the date shown.">
          <ul className="acct-devices">
            {trusted.data!.map((t) => (
              <li key={t.id}>
                <Icon.shield />
                <span className="acct-devices__text">
                  <strong>{t.browser}</strong>
                  <small>Trusted until {when(t.expiresAt)}</small>
                </span>
                <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void run(t.id, () => rootApi(`/me/trusted-browsers/${t.id}`, { method: "DELETE" }), "Browser forgotten")}>
                  Forget
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <ConfirmDialog
        open={all}
        title="Log out of all other devices?"
        text="Every other browser logged in to Mivio is logged out. You stay logged in here."
        confirmLabel="Log out"
        tone="danger"
        busy={busy === "all"}
        onConfirm={() => void run("all", () => rootApi("/me/sessions/sign-out-others", { method: "POST" }), "Every other device logged out")}
        onCancel={() => setAll(false)}
      />
    </Annotated>
  );
}

function Activity({ when }: { when: (iso: string | null) => string }) {
  const q = useQuery({ queryKey: ["me", "activity"], queryFn: () => rootApi<{ events: EventRow[] }>("/me/activity").then((r) => r.events) });
  return (
    <Annotated title="Recent activity" text="Logins and changes to your login's security, newest first.">
      <Card flush>
        {q.isPending ? (
          <p className="acct-pad muted">Loading…</p>
        ) : (
          <ul className="rows">
            {(q.data ?? []).slice(0, 15).map((e, i) => (
              <li key={i}>
                <span className="rows__text">
                  <strong>{EVENT_TEXT[e.kind] ?? e.kind}</strong>
                  <small>
                    {when(e.at)}
                    {typeof e.detail.browser === "string" ? ` · ${e.detail.browser}` : ""}
                  </small>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Annotated>
  );
}

/** Logs out and goes to the log-in page. */
export function useLogOut() {
  const { signOut } = useSession();
  return () => void signOut().then(() => go("/sign-in"));
}
